import { ref, computed } from 'vue';
import api from '@/lib/axios';
import { useRouter } from 'vue-router';

const user = ref(null);
const isLoading = ref(false);
const isAuthenticated = computed(() => !!user.value);

// Session storage keys for 2FA flow
const SESSION_2FA_TOKEN = '2fa_token';
const SESSION_2FA_SETUP_REQUIRED = '2fa_setup_required';

export function useAuth() {
    const router = useRouter();

    // Check if 2FA setup is required (from sessionStorage)
    const is2FASetupRequired = () => {
        return sessionStorage.getItem(SESSION_2FA_SETUP_REQUIRED) === 'true';
    };

    // Set 2FA setup required flag
    const set2FASetupRequired = (required) => {
        if (required) {
            sessionStorage.setItem(SESSION_2FA_SETUP_REQUIRED, 'true');
        } else {
            sessionStorage.removeItem(SESSION_2FA_SETUP_REQUIRED);
        }
    };

    // Clear all 2FA session flags (call after successful 2FA completion)
    const clear2FASession = () => {
        sessionStorage.removeItem(SESSION_2FA_TOKEN);
        sessionStorage.removeItem(SESSION_2FA_SETUP_REQUIRED);
    };

    const fetchUser = async () => {
        try {
            const { data } = await api.get('/api/v1/auth/user');
            user.value = data.data || data;

            // Sync 2FA setup required flag from user data
            if (user.value?.setup_2fa_required) {
                set2FASetupRequired(true);
            }

            return user.value;
        } catch (error) {
            user.value = null;
            throw error;
        }
    };

    const login = async (credentials) => {
        isLoading.value = true;
        try {
            await api.get('/sanctum/csrf-cookie');
            const { data } = await api.post('/api/v1/auth/login', credentials);

            // Check if 2FA verification is required (user has 2FA configured)
            if (data.requires_2fa || data.data?.two_factor_required) {
                const token = data.token || data.data?.token;
                sessionStorage.setItem(SESSION_2FA_TOKEN, token);
                router.push('/2fa-verify');
                return;
            }

            // Store user data from login response
            if (data.data && data.data.user) {
                user.value = data.data.user;

                // Check if 2FA setup is required (enforced by admin)
                if (data.data.user.setup_2fa_required) {
                    set2FASetupRequired(true);
                    router.push('/2fa-setup');
                    return;
                }
            } else {
                await fetchUser();
            }

            // Clear any stale 2FA flags and redirect to app
            clear2FASession();
            const roles = user.value?.roles || [];
            const role = user.value?.role || null;
            if (roles.includes('superadmin')) {
                router.push('/backoffice');
            } else if (user.value?.organization?.onboarding_completed === false) {
                router.push('/onboarding');
            } else if ((role === 'employee' || roles.includes('employee')) && !roles.includes('admin') && !roles.includes('manager')) {
                // Employee-only users go to their portal
                const empId = user.value?.employee_id;
                if (empId) {
                    router.push(`/app/employee/${empId}/detail/utilization`);
                } else {
                    router.push('/app/settings/profile');
                }
            } else {
                router.push('/trends');
            }
        } catch (error) {
            console.error('Error en login:', error);
            throw error;
        } finally {
            isLoading.value = false;
        }
    };

    const logout = async () => {
        isLoading.value = true;
        try {
            await api.post('/api/v1/auth/logout');
            user.value = null;
            clear2FASession();
            router.push('/');
        } catch (error) {
            console.error('Error en logout:', error);
        } finally {
            isLoading.value = false;
        }
    };

    return {
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        fetchUser,
        is2FASetupRequired,
        set2FASetupRequired,
        clear2FASession
    };
}
